import './components/Pleri/components/ChatBox/ChatBox.css';
import './components/onboarding/registry/registerSkills';
import './components/onboarding/registry/registerWorkspaces';
import React, { useEffect } from 'react';
// import '@plerionhq/design-system/tailwind.css';
import './css/index.css';

import {
  RouterProvider,
  createMemoryHistory,
  // createBrowserHistory,
  createRouter,
} from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { PleriMessageStreamListener } from './components/Pleri/components/PleriMessageStreamListener';
import { registerPleriApi } from './components/Pleri/api/registerPleriApi';
import { usePleriStatus } from './components/onboarding/apis/pleri-status';
import { useIsAdmin } from './components/onboarding-new/api';
import { useChatStore } from './components/Pleri/store/chatStore';
import { usePleriRender } from './components/Pleri/components';
import { useLayoutStore } from './store/layoutStore';
import { OnboardingPrefetcher } from './components/onboarding-new/OnboardingPrefetcher';
import { PleriAnnouncements } from './components/Pleri/components/PleriAnnouncements';

const memoryHistory = createMemoryHistory({ initialEntries: ['/trigger'] });

// Lazy initialization to avoid circular dependency issues
let routerInstance: ReturnType<typeof createRouter> | null = null;

const getRouter = (): ReturnType<typeof createRouter> => {
  if (!routerInstance) {
    routerInstance = createRouter({
      routeTree,
      history: memoryHistory,
      basepath: '/pleri',
    });
  }
  return routerInstance;
};

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}

// Export a getter that lazily initializes the router
// Using a Proxy to ensure the router is only created when first accessed
export const pleriRouter = new Proxy({} as ReturnType<typeof createRouter>, {
  get(_target, prop) {
    const router = getRouter();
    const value = router[prop as keyof typeof router];
    // If it's a function, bind it to the router instance
    if (typeof value === 'function') {
      return value.bind(router);
    }
    return value;
  },
}) as ReturnType<typeof createRouter>;

// Component to handle navigation logic
const PleriContent = () => {
  const { data: pleriStatus, isLoading: isLoadingStatus } = usePleriStatus();
  const { data: status, isLoading, isError } = usePleriStatus();
  const requiresOnboarding = !isLoading && !isError && !status?.isOnboarded;
  const { isOpen, renderType, open } = usePleriRender();
  const isOrgAdmin = useIsAdmin();
  useEffect(() => {
    registerPleriApi();
  }, []);

  useEffect(() => {
    useChatStore.getState().resetState();
  }, []);

  useEffect(() => {
    if (!isOpen && renderType !== 'extension') {
      pleriRouter.navigate({ to: '/trigger' });
      return;
    }

    if (requiresOnboarding) {
      pleriRouter.navigate({ to: '/onboarding' });
      return;
    }

    if (renderType === 'extension') {
      pleriRouter.navigate({
        to: '/c/$conversationId',
        params: { conversationId: 'active' },
      });
    }
  }, [isOpen, renderType, requiresOnboarding]);

  // Check browser URL and navigate memory router accordingly
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const path = searchParams.get('p');

    // Check if URL has p=pleri parameter
    if (path?.startsWith('/pleri/')) {
      useLayoutStore.getState().setMiniMode(false);

      // Extract the path after /pleri/ and navigate
      const pleriPath = path.replace('/pleri', '') || '/';
      const routerSearch = Object.fromEntries(searchParams.entries());
      open();
      pleriRouter.navigate({
        to: pleriPath,
        search: routerSearch,
      });
    }
  }, [open]);

  // Don't show anything while loading status
  if (isLoadingStatus) {
    return null;
  }

  // Don't show if Pleri is not enabled for this organization
  if (!pleriStatus?.isEnabled || !pleriStatus?.showPleriInDashboard) {
    return null;
  }

  return (
    <>
      {isOrgAdmin && <PleriAnnouncements />}
      <OnboardingPrefetcher />
      <PleriMessageStreamListener />
      <RouterProvider router={getRouter()} />
    </>
  );
};

export const Pleri = () => {
  return (
    <div id="search-app">
      <div id="pleri-chat-box" className="antialiased">
        <PleriContent />
      </div>
    </div>
  );
};
